import { useState, useCallback } from "react";

export interface ClassResult {
  classId: number;
  classTitle: string;
  scores: { creativity: number; specificity: number; completeness: number };
  avgScore: number;
  pointsEarned: number;
  expertVerified: boolean;
  completedAt: string;
  completedAtMs?: number;
}

export interface GrowStore {
  totalPoints: number;
  earnedBadgeIds: string[];
  completedClasses: ClassResult[];
  recentlyEarned: { points: number; badge: string | null } | null;
}

const BASE_POINTS = 2400;

const DEFAULT_STORE: GrowStore = {
  totalPoints: BASE_POINTS,
  earnedBadgeIds: ["badge-7day", "badge-speed", "badge-seed", "badge-ai", "badge-10"],
  completedClasses: [],
  recentlyEarned: null,
};

function load(): GrowStore {
  try {
    const raw = localStorage.getItem("growseo_store");
    if (raw) return { ...DEFAULT_STORE, ...JSON.parse(raw) };
  } catch (e) {
    console.warn("GrowStore load error", e);
  }
  return DEFAULT_STORE;
}

function save(store: GrowStore) {
  try {
    localStorage.setItem("growseo_store", JSON.stringify(store));
  } catch (e) {
    console.warn("GrowStore save error", e);
  }
}

export function calcPointsFromScore(avg: number, expertVerified: boolean): number {
  let pts = avg >= 90 ? 200 : avg >= 80 ? 150 : avg >= 70 ? 100 : 70;
  if (expertVerified) pts += 50;
  return pts;
}

export function useGrowStore() {
  const [store, setStore] = useState<GrowStore>(load);

  const addClassResult = useCallback((result: ClassResult) => {
    setStore((prev) => {
      const newTotal = prev.totalPoints + result.pointsEarned;
      const newCompleted = [
        result,
        ...prev.completedClasses.filter((c) => c.classId !== result.classId),
      ];

      // Badge unlock logic
      const newBadges = [...prev.earnedBadgeIds];
      let newBadge: string | null = null;

      if (!newBadges.includes("badge-first") && newCompleted.length >= 1) {
        newBadges.push("badge-first");
        newBadge = "🎓 첫 졸업";
      } else if (!newBadges.includes("badge-3class") && newCompleted.length >= 3) {
        newBadges.push("badge-3class");
        newBadge = "📚 열공중";
      } else if (!newBadges.includes("badge-expert") && result.expertVerified) {
        newBadges.push("badge-expert");
        newBadge = "👑 전문가 인증";
      } else if (!newBadges.includes("badge-highscore") && result.avgScore >= 88) {
        newBadges.push("badge-highscore");
        newBadge = "⭐ 우등생";
      }

      const next: GrowStore = {
        totalPoints: newTotal,
        earnedBadgeIds: newBadges,
        completedClasses: newCompleted,
        recentlyEarned: { points: result.pointsEarned, badge: newBadge },
      };
      save(next);
      return next;
    });
  }, []);

  const clearRecentlyEarned = useCallback(() => {
    setStore((prev) => {
      const next = { ...prev, recentlyEarned: null };
      save(next);
      return next;
    });
  }, []);

  const isClassCompleted = useCallback(
    (classId: number) => store.completedClasses.some((c) => c.classId === classId),
    [store.completedClasses]
  );

  return { store, addClassResult, clearRecentlyEarned, isClassCompleted };
}
